import React from 'react';
import { motion } from 'framer-motion';
import { 
  Map, 
  Layers, 
  FileSpreadsheet, 
  Wrench, 
  Zap, 
  ArrowRight,
  Sparkles,
  Sun
} from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    { 
      id: '01',
      icon: Map, 
      phase: 'ASSESSMENT',
      title: 'Spatial Mapping', 
      description: 'We scan your roof layout using high-resolution drone & satellite tools.' 
    },
    { 
      id: '02',
      icon: Layers, 
      phase: 'ENGINEERING',
      title: '3D Modeling', 
      description: 'Our engineers draft blueprint charts matching your daily load consumption.' 
    },
    { 
      id: '03',
      icon: FileSpreadsheet, 
      phase: 'FINANCIALS',
      title: 'Precision Quote', 
      description: 'Receive an all-inclusive cost matrix detailing panel ROI and subsidies.' 
    },
    { 
      id: '04',
      icon: Wrench, 
      phase: 'INSTALLATION',
      title: 'Rapid Mounting', 
      description: 'Certified technicians securely install structural mounts safely within 48 hours.' 
    },
    { 
      id: '05',
      icon: Zap, 
      phase: 'COMMISSION',
      title: 'Grid Sync & Go', 
      description: 'Post net-metering sync with local DISCOMs, your green power station goes live.' 
    },
  ];

  return (
    <section id="process" className="py-12 sm:py-16 lg:py-20 bg-[#F8FAFC] relative overflow-hidden border-y border-slate-200/60 font-sans">
      
      {/* Subtle Ambient Background */}
      <div className="absolute top-0 right-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#0D3F7F]/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🌟 1. SECTION HEADER 🌟 */}
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3.5 py-1 sm:px-4 sm:py-1.5 rounded-full mb-3 sm:mb-4 shadow-sm"
          >
            <Sun className="w-3.5 h-3.5 text-[#F7941D]" />
            <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#0D3F7F] uppercase">
              Execution Flowchart
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0F172A] tracking-tight leading-tight"
          >
            Our Professional <span className="text-[#F7941D]">Execution Flow</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="text-slate-500 text-xs sm:text-[14px] font-medium leading-relaxed mt-3 px-2 sm:px-0"
          >
            From satellite assessment to final grid integration, we handle every step with precision and transparency.
          </motion.p>
        </div>

        {/* 🌟 2. ADAPTIVE TIMELINE SYSTEM (Vertical on Mobile / Horizontal on Desktop) 🌟 */}
        <div className="relative pl-4 sm:pl-8 lg:pl-0">
          
          {/* A. Vertical Connecting Line (Mobile & Tablet only) */}
          <div className="lg:hidden absolute left-[27px] sm:left-[43px] top-6 bottom-6 w-[2px] bg-slate-200 z-0">
            <motion.div
              initial={{ height: "0%" }}
              whileInView={{ height: "100%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="w-full bg-gradient-to-b from-[#0D3F7F] via-[#1E5CB3] to-[#F7941D]"
            />
          </div>

          {/* B. Desktop Connecting Line (Desktop only) */}
          <div className="hidden lg:block absolute top-[28px] left-[5%] right-[5%] h-[2px] bg-slate-200 z-0">
            <motion.div 
              initial={{ width: "0%" }}
              whileInView={{ width: "100%" }}
              viewport={{ once: true }}
              transition={{ duration: 2, ease: "easeInOut" }}
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#0D3F7F] via-[#1E5CB3] to-[#F7941D] shadow-[0_0_8px_#F7941D]"
            />
          </div>

          {/* Timeline Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-4 relative z-10">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20, y: 15 }}
                  whileInView={{ opacity: 1, x: 0, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="group relative flex flex-row items-start text-left gap-4 sm:gap-6 lg:flex-col lg:items-center lg:text-center lg:gap-0 bg-white lg:bg-transparent lg:hover:bg-white p-4 sm:p-5 lg:p-4 rounded-2xl transition-all duration-300 hover:shadow-[0_10px_30px_rgba(13,63,127,0.06)] border border-slate-100 lg:border-transparent lg:hover:border-slate-200"
                >
                  {/* Icon Circle */}
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white border-4 border-[#F8FAFC] group-hover:border-white shadow-md flex items-center justify-center relative z-10 flex-shrink-0 transition-transform duration-300 lg:group-hover:-translate-y-2 lg:group-hover:shadow-[0_8px_20px_rgba(247,148,29,0.3)] lg:mb-4">
                    <div className="w-full h-full rounded-full bg-[#0D3F7F] flex items-center justify-center text-white group-hover:bg-[#F7941D] transition-colors duration-300">
                      <StepIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    {/* Floating Step Number */}
                    <div className="absolute -top-1.5 -right-1.5 bg-white text-[#0D3F7F] text-[9px] sm:text-[10px] font-black px-1.5 py-0.5 rounded border border-slate-200 shadow-sm">
                      {step.id}
                    </div>
                  </div>

                  {/* Text Content */}
                  <div className="flex flex-col justify-center">
                    <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#F7941D] uppercase mb-1">
                      {step.phase}
                    </span>
                    <h3 className="text-sm sm:text-[15px] font-bold text-[#0F172A] mb-1.5 lg:mb-2 group-hover:text-[#0D3F7F] transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-[11px] sm:text-[12px] text-slate-500 leading-relaxed font-medium">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* 🌟 3. COMPACT BOTTOM CTA BANNER 🌟 */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 sm:mt-16 bg-[#050B14] p-5 sm:p-8 rounded-2xl border border-[#1E5CB3]/30 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden"
        >
          {/* Internal Glow */}
          <div className="absolute right-0 top-0 w-48 sm:w-64 h-48 sm:h-64 bg-[#F7941D]/10 rounded-full blur-[50px] sm:blur-[60px] pointer-events-none" />
          
          <div className="text-center md:text-left relative z-10 w-full md:w-auto">
            <div className="inline-flex items-center gap-1.5 bg-[#F7941D]/15 px-3 py-1 rounded-full text-[9px] sm:text-[10px] font-bold text-[#FFC107] tracking-wider uppercase mb-2 border border-[#F7941D]/30">
              <Sparkles className="w-3 h-3" />
              <span>Free 3D Virtual Render</span>
            </div>
            <h4 className="text-base sm:text-lg lg:text-xl font-black text-white tracking-tight">
              See how solar looks on your roof!
            </h4>
          </div>
          
          <a 
            href="#contact" 
            className="group relative inline-flex items-center justify-center w-full md:w-auto overflow-hidden rounded-full p-[2px] font-bold transition-all duration-300 hover:scale-[1.02] active:scale-95 shrink-0 z-10"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-[#F7941D] via-[#FFC107] to-[#F7941D] rounded-full opacity-70 blur-md group-hover:opacity-100 transition-opacity animate-pulse" />
            <span className="relative flex items-center justify-center gap-2 bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white text-[12px] sm:text-[13px] font-bold px-5 sm:px-7 py-3 rounded-full tracking-wide shadow-inner border border-white/20 w-full md:w-auto">
              <span>Request Render</span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default HowItWorks;